import { usePutCancelOrder } from "@multicines/services";
import { useMoviesStore, useProductsStore } from "@multicines/stores";
import { deleteShoppingCart } from "artisn/shopping-cart";
import MovieSVG from "images/movie.svg";
import React, { useState } from "react";
import { useQueryClient } from "react-query";

import Styles from "./ExitReservation.styles";
import { ExitReservationProps as Props } from "./ExitReservation.types";
import Button from "../Button/Button";
import usePurchaseEvents from "components/checkout/Checkout/hooks/usePurchaseEvents";
import { getAuth } from "config/artisn.config";
import { notify } from "config/bugsnag.config";
import CONSTANTS from "config/constants";
import useAnalytics from "hooks/analytics/useAnalytics";
import useCancelReservation from "hooks/useCancelReservation";
import useI18n from "hooks/useI18n";
import useTalkShop from "hooks/useTalkShop";
import { useAuthStore } from "stores/auth/auth.store";
import { useCheckoutStore } from "stores/checkout/checkout.store";

const { SHOPPING_CART_DEFAULT_NAME, ACCOUNT_ID } = CONSTANTS.ARTISN;

const ExitReservation: React.FC<Props> = props => {
  const { onClose, shouldCancel = true } = props;
  const queryClient = useQueryClient();
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const { navigateWithTalkShop } = useTalkShop();
  const analyticsHook = useAnalytics({
    isSnackWorkflow: false
  });
  const t = useI18n();
  const { logPurchaseFail } = usePurchaseEvents();
  const { cancelReservation, isLoading } = useCancelReservation();
  const resetSeatsToAllocate = useMoviesStore(
    store => store.resetSeatsToAllocate
  );
  const setReservationExpiration = useMoviesStore(
    store => store.setReservationExpiration
  );
  const resetMovies = useMoviesStore(store => store.resetMovies);
  const setSnackRestrictionCard = useProductsStore(
    store => store.setSnackRestrictionCard
  );
  const { mutateAsync: putCancelOrder, reset } = usePutCancelOrder({
    uid,
    isAnonymous
  });
  const placedOrder = useCheckoutStore(state => state.placedOrder);
  const [disable, setDisable] = useState(false);
  const [error, setError] = useState("");

  const { logCancelProcess } = analyticsHook;

  const exitHandler = async () => {
    if (disable) return;
    setError("");
    try {
      setDisable(true);
      const deletedShoppingCart = await deleteShoppingCart({
        shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
        anonymous: isAnonymous!,
        accountId: ACCOUNT_ID,
        customerId: uid!
      });
      if (deletedShoppingCart && placedOrder) {
        logPurchaseFail("El proceso se ha abandonado", deletedShoppingCart);
        await putCancelOrder(placedOrder.id);
        reset();
      }
      if (shouldCancel) await cancelReservation();
      // Refresh the token
      await getAuth.currentUser?.getIdToken(true);
      queryClient.refetchQueries([uid, "loyalty"]);
      resetSeatsToAllocate();
      setReservationExpiration(undefined);
      resetMovies();
      setSnackRestrictionCard(undefined);
      setDisable(false);
      logCancelProcess();
      navigateWithTalkShop("/billboard");
    } catch (error) {
      notify(error, "exitHandler - ExitReservation");
      setError(t.movie.cancelReservationError);
      setDisable(false);
    }
  };

  return (
    <Styles className="ExitReservation">
      <div className="ExitReservation__header">
        <MovieSVG />
        <p className="ExitReservation__title">{t.movie.saveReservation}</p>
        <p className="ExitReservation__description">{t.movie.exitMessage}</p>
      </div>
      {error ? (
        <p className="ExitReservation__error">
          {t.movie.cancelReservationError}
        </p>
      ) : null}
      <div className="ExitReservation__buttons">
        <Button
          mode="PRIMARY"
          className="ExitReservation__buttons__top"
          onClick={onClose}
        >
          {t.movie.continueReservation}
        </Button>
        <Button
          mode="SECONDARY"
          className="ExitReservation__buttons__bottom"
          onClick={exitHandler}
          isLoading={disable || isLoading}
          disabled={disable || isLoading}
        >
          {t.movie.exit}
        </Button>
      </div>
    </Styles>
  );
};

export default ExitReservation;
